import { useState, useEffect } from "react";
import { jwtFetch } from "./utils/apiClient";
import { useParams, useNavigate } from "react-router-dom";
import { format, addDays } from "date-fns";
import { Modal, Accordion, Dropdown } from "react-bootstrap";
import Select from "react-select";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { config, priceFormat, shortenUrl } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";

const QuotationDetail = () => {
  const { id_quotation } = useParams();
  const [quotation, setQuotation] = useState(null);
  const [quotationItems, setQuotationItems] = useState([]);
  const [paymentSimulations, setPaymentSimulations] = useState(null);
  const [salesOrder, setSalesOrder] = useState(null);
  const [closeLostRemarks, setCloseLostRemarks] = useState("");
  const [iouList, setIouList] = useState(null);
  const [termList, setTermList] = useState(null);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [showCloseLost, setShowCloseLost] = useState(false);
  const [showSignRequestModal, setShowSignRequestModal] = useState(false);
  const [isSendingSignRequest, setIsSendingSignRequest] = useState(false);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [contactOptions, setContactOptions] = useState([]);
  const [selectedContactOption, setSelectedContactOption] = useState(null);
  const [relatedContract, setRelatedContract] = useState(null);
  const [relatedSchedule, setRelatedSchedule] = useState(null);
  const [isLoadingRelatedContractData, setIsLoadingRelatedContractData] = useState(false);
  const [signRecipient, setSignRecipient] = useState({
    contact_person_id: null,
    cperson_name: "",
    cperson_email: "",
    cperson_position: "",
  });
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [createdByUser, setCreatedByUser] = useState(null);

  const navigate = useNavigate();

  const crmUser = JSON.parse(localStorage.getItem("crmUser"));

  useEffect(() => {
    if (id_quotation) {
      getQuotation();
      getPaymentSimulations();
      getSalesOrder();
      getIou();
    }
  }, [id_quotation]);

  useEffect(() => {
    if (approvalStatus === "Rejected") {
      setApprovalRemarks("");
      setShowApprovalModal(true);
    } else if (approvalStatus === "Approved") {
      submitApproval();
    }
  }, [approvalStatus]);

  const getQuotation = async () => {
    try {
      const fetchQuotation = await jwtFetch(config.ENDPOINT + "get/quotation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_quotation: id_quotation,
        }),
      });
      const responseGetQuotation = await fetchQuotation.json();
      if (responseGetQuotation.status === 200) {
        const quotationData = responseGetQuotation.data[0];
        setQuotation(quotationData);
        if (quotationData.created_by) {
          getCreatedByUser(quotationData.created_by);
        }
        getRelatedContractData(quotationData);
        let items = [];
        for (let i in quotationData.quotation_item_list) {
          const companyLicence = await getCompanyLicence(
            quotationData.company_id,
            quotationData.quotation_item_list[i].product_id,
            quotationData.quotation_item_list[i].product_domain,
          );
          if (companyLicence) {
            quotationData.quotation_item_list[i].licence_start_period = companyLicence.licence_start_period;
            quotationData.quotation_item_list[i].licence_end_period = companyLicence.licence_end_period;
          }
          quotationData.quotation_item_list[i].companies = [
            {
              id_quotation_item: quotationData.quotation_item_list[i].quotation_item_id,
              company_id: quotationData.subcompany_id ? quotationData.subcompany_id : "",
              company_name: quotationData.subcompany_id ? quotationData.subcompany_name : "",
              product_qty: quotationData.quotation_item_list[i].product_qty,
              product_cost_original: quotationData.quotation_item_list[i].product_cost_original,
              product_cost: quotationData.quotation_item_list[i].product_cost,
              product_price_original: quotationData.quotation_item_list[i].product_price_original,
              product_price: quotationData.quotation_item_list[i].product_price,
              product_subtotal: quotationData.quotation_item_list[i].product_subtotal,
              product_discount_price: quotationData.quotation_item_list[i].product_discount_price,
              product_discount_rate: quotationData.quotation_item_list[i].product_discount_rate,
              product_discount_prorate: quotationData.quotation_item_list[i].product_discount_prorate,
              product_margin_price: quotationData.quotation_item_list[i].product_margin_price,
              product_margin_rate: quotationData.quotation_item_list[i].product_margin_rate,
              product_total: quotationData.quotation_item_list[i].product_total,
              product_grand_total: quotationData.quotation_item_list[i].product_grand_total,
              product_prorate: 100,
              contract_long_period: quotationData.quotation_item_list[i].contract_long_period,
            },
          ];
          items.push(quotationData.quotation_item_list[i]);
          setQuotationItems([...items]);
        }
      } else {
        alert(responseGetQuotation.message);
      }
    } catch (error) {
      console.error('Error in getQuotation:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getCreatedByUser = async (userId) => {
    try {
      const fetchUser = await jwtFetch(config.ENDPOINT + "get/user", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_keyid: userId,
        }),
      });
      const responseUser = await fetchUser.json();
      if (responseUser.status === 200 && responseUser.data.length > 0) {
        setCreatedByUser(responseUser.data[0]);
      }
    } catch (error) {
      console.error("Error fetching created_by user:", error);
    }
  };

  const getRelatedContractData = async (quotationData) => {
    setIsLoadingRelatedContractData(true);
    let scheduleData = null;
    let contractData = null;
    try {
      if (quotationData.contract_schedule_id) {
        const fetchSchedule = await jwtFetch(config.ENDPOINT + "get/contract_schedule", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id_contract_schedule: quotationData.contract_schedule_id,
          }),
        });
        const responseSchedule = await fetchSchedule.json();
        if (responseSchedule.status === 200 && responseSchedule.data.length > 0) {
          scheduleData = responseSchedule.data[0];
        }
      }

      if (scheduleData?.contract_id) {
        const fetchContract = await jwtFetch(config.ENDPOINT + "get/contract", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id_contract: scheduleData.contract_id,
          }),
        });
        const responseContract = await fetchContract.json();
        if (responseContract.status === 200 && responseContract.data.length > 0) {
          contractData = responseContract.data[0];
        }
      }

      if (!contractData) {
        const fetchMasterContract = await jwtFetch(config.ENDPOINT + "get/contract", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            quotation_id: quotationData.id_quotation,
          }),
        });
        const responseMasterContract = await fetchMasterContract.json();
        if (responseMasterContract.status === 200 && responseMasterContract.data.length > 0) {
          contractData = responseMasterContract.data[0];
        }
      }
    } finally {
      setRelatedSchedule(scheduleData);
      setRelatedContract(contractData);
      setIsLoadingRelatedContractData(false);
    }
  };

  const getPaymentSimulations = async () => {
    try {
      const fetchPaymentSimulations = await jwtFetch(
        config.ENDPOINT + "get/quotation_payment_simulation",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            quotation_id: id_quotation,
          }),
        },
      );
      const responsePaymentSimulations = await fetchPaymentSimulations.json();
      if (responsePaymentSimulations.status === 200) {
        setPaymentSimulations(responsePaymentSimulations.data);
      } else {
        alert(responsePaymentSimulations.message);
      }
    } catch (error) {
      console.error('Error in getPaymentSimulations:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getSalesOrder = async () => {
    try {
      const fetchSalesOrder = await jwtFetch(config.ENDPOINT + "get/so", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          quotation_id: id_quotation,
        }),
      });
      const responseSalesOrder = await fetchSalesOrder.json();
      if (responseSalesOrder.status === 200) {
        setSalesOrder(responseSalesOrder.data[0]);
      } else {
        alert(responseSalesOrder.message);
      }
    } catch (error) {
      console.error('Error in getSalesOrder:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getIou = async () => {
    try {
      const fetchIou = await jwtFetch(config.ENDPOINT + "get/iou", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          quotation_id: id_quotation,
        }),
      });
      const responseIou = await fetchIou.json();
      if (responseIou.status === 200) {
        let terms = [];
        for (let i in responseIou.data) {
          if (responseIou.data[i].iou_items) {
            responseIou.data[i].iou_items = await getIouItemList(responseIou.data[i].id_iou);
          }
          terms[responseIou.data[i].term_number] = terms[responseIou.data[i].term_number] || [];
          terms[responseIou.data[i].term_number].push(responseIou.data[i]);
        }
        setTermList(terms);
        setIouList(responseIou.data);
      } else {
        alert(responseIou.message);
      }
    } catch (error) {
      console.error('Error in getIou:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getIouItemList = async (iou_id) => {
    try {
      const requestIouList = {
        iou_id: iou_id,
      };
      const fetchIouList = await jwtFetch(config.ENDPOINT + "get/iou_item", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestIouList),
      });
      const responseIouList = await fetchIouList.json();
      if (responseIouList.status === 200) {
        const newIouItems = [];
        for (let i in responseIouList.data) {
          let newIouItem = responseIouList.data[i];
          const companyLicence = await getCompanyLicence(
            responseIouList.data[i].company_id,
            responseIouList.data[i].product_id,
            responseIouList.data[i].product_domain,
          );
          if (companyLicence) {
            newIouItem.licence_start_period = companyLicence.licence_start_period;
            newIouItem.licence_end_period = companyLicence.licence_end_period;
            newIouItem.action = "Update";
          }
          newIouItems.push(newIouItem);
        }
        return newIouItems;
      } else {
        alert(responseIouList.message);
      }
    } catch (error) {
      console.error('Error in getIouItemList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getCompanyLicence = async (companyId, productId, licenceDomain) => {
    try {
      const requestCompanyLicence = {
        company_id: companyId,
        product_id: productId,
        licence_domain: licenceDomain,
      };
      const fetchCompanyLicence = await jwtFetch(config.ENDPOINT + "get/clients_company_licence", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestCompanyLicence),
      });
      const responseCompanyLicence = await fetchCompanyLicence.json();
      if (responseCompanyLicence.status === 200) {
        if (responseCompanyLicence.data.length > 0) {
          return responseCompanyLicence.data[0];
        } else {
          return null;
        }
      } else {
        alert(responseCompanyLicence.message);
      }
    } catch (error) {
      console.error('Error in getCompanyLicence:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const copyItem = (itemIndex) => {
    let description = quotationItems[itemIndex].product_display_name;
    description += "\n\n";
    description += quotationItems[itemIndex].product_display_description;
    description += "\n\n";
    if (iouList) {
      description +=
        iouList[0].service_type === "Additional"
          ? "Period - Current Active Subscription: " +
          (format(new Date(iouList[0].iou_items[itemIndex].licence_start_period), "d MMMM yyyy") +
            " - " +
            format(new Date(iouList[0].iou_items[itemIndex].licence_end_period), "d MMMM yyyy")) +
          "\n"
          : "";
      description +=
        "Period - for this QT Order: " +
        (format(new Date(iouList[0].iou_items[itemIndex].start_term_period), "d MMMM yyyy") +
          " - " +
          format(new Date(iouList[0].iou_items[itemIndex].end_term_period), "d MMMM yyyy")) +
        "\n";
    }
    navigator.clipboard.writeText(description);
  };

  const editStatus = async (status) => {
    try {
      setIsLoadingSubmit(true);
      const crmUser = JSON.parse(localStorage.getItem("crmUser"));
      let approval = quotation.quotation_approval;
      if (status === "Open") {
        let isRenewal = quotation.quotation_item_list.some((item) => item.service_type === "Renewal");
        if (isRenewal) {
          if (quotation.quotation_margin_rate < 5) {
            approval = "Pending Approval 1";
          } else if (quotation.quotation_margin_rate < 10) {
            approval = "Pending Approval";
          } else {
            approval = "Approved";
          }
        } else {
          if (quotation.quotation_margin_rate <= 10) {
            approval = "Pending Approval 1";
          } else if (quotation.quotation_margin_rate < 20) {
            approval = "Pending Approval";
          } else {
            approval = "Approved";
          }
        }
      } else if (status === "Canceled") {
        if (salesOrder) {
          const fetchCancelSO = await jwtFetch(config.ENDPOINT + "update/so", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              id_so: salesOrder.id_so,
              so_status: "Canceled",
              updated_by: crmUser.user_keyid,
              updated_on: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
            }),
          });
          const responseCancelSO = await fetchCancelSO.json();
          if (responseCancelSO.status !== 200) {
            alert("Failed to cancel Sales Order");
            setIsLoadingSubmit(false);
            return;
          }
        }
      }
      const fetchEditQuotation = await jwtFetch(config.ENDPOINT + "update/quotation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_quotation: id_quotation,
          quotation_status: status,
          quotation_approval: approval,
          updated_by: crmUser.user_keyid,
          updated_on: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
        }),
      });
      const responseEditQuotation = await fetchEditQuotation.json();
      if (responseEditQuotation.status === 200) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error in editStatus:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingSubmit(false);
    }
  };

  const reopenStatus = async () => {
    try {
      setIsLoadingSubmit(true);
      const fetchEditQuotation = await jwtFetch(config.ENDPOINT + "update/quotation_status", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_quotation: id_quotation,
          quotation_status: "Open",
        }),
      });
      const responseEditQuotation = await fetchEditQuotation.json();
      if (responseEditQuotation.status === 200) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error in reopenStatus:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingSubmit(false);
    }
  };

  const isCurrentUserApprover = () => {
    if (!quotation || !createdByUser || !crmUser) return false;

    if (quotation.quotation_approval === "Pending Approval" || quotation.quotation_approval === "Pending Approval 1") {
      return createdByUser.approval_1 && createdByUser.approval_1 === crmUser.user_keyid;
    } else if (quotation.quotation_approval === "Pending Approval 2") {
      return createdByUser.approval_2 && createdByUser.approval_2 === crmUser.user_keyid;
    }
    return false;
  };

  const submitApproval = async () => {
    try {
      setIsLoadingSubmit(true);
      const crmUser = JSON.parse(localStorage.getItem("crmUser"));
      const requestEditQuotation = {
        id_quotation: id_quotation,
        quotation_status: "Open",
        quotation_approval: approvalStatus,
        updated_by: crmUser.user_keyid,
        updated_on: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
      };
      if (quotation.quotation_approval === "Pending Approval") {
        requestEditQuotation.approval_1_remarks = approvalRemarks;
      } else if (quotation.quotation_approval === "Pending Approval 1") {
        requestEditQuotation.quotation_approval = approvalStatus === "Approved" ? "Pending Approval 2" : "Rejected";
        requestEditQuotation.approval_1_remarks = approvalRemarks;
      } else if (quotation.quotation_approval === "Pending Approval 2") {
        requestEditQuotation.approval_2_remarks = approvalRemarks;
      }
      const fetchEditQuotation = await jwtFetch(config.ENDPOINT + "update/quotation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestEditQuotation),
      });
      const responseEditQuotation = await fetchEditQuotation.json();
      if (responseEditQuotation.status === 200) {
        window.location.reload();
      }
      setIsLoadingSubmit(false);
    } catch (error) {
      console.error('Error in submitApproval:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingSubmit(false);
    }
  };

  const printQuotation = async () => {
    window.open("/quotation/print/" + id_quotation, "_blank");
  };

  const getContacts = async (companyId, selectedContactPersonId = null) => {
    if (!companyId) {
      setContactOptions([]);
      setSelectedContactOption(null);
      return;
    }

    setIsLoadingContacts(true);
    try {
      const fetchContacts = await jwtFetch(config.ENDPOINT + "get/contact_person", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          company_id: companyId,
        }),
      });
      const responseContacts = await fetchContacts.json();
      if (responseContacts.status === 200) {
        const options = (responseContacts.data || []).map((contact) => ({
          value: contact.id_contact_person,
          label: `${contact.cperson_name || "Unnamed"}${contact.cperson_email ? ` (${contact.cperson_email})` : ""
            }`,
          contact,
        }));
        setContactOptions(options);

        if (selectedContactPersonId) {
          const selectedOption = options.find(
            (option) => Number(option.value) === Number(selectedContactPersonId),
          );
          setSelectedContactOption(selectedOption || null);
        } else {
          setSelectedContactOption(null);
        }
      } else {
        setContactOptions([]);
        setSelectedContactOption(null);
        alert(responseContacts.message);
      }
    } finally {
      setIsLoadingContacts(false);
    }
  };

  const openSignRequestModal = async () => {
    const selectedContactPersonId = quotation?.contact_person_id || null;
    setSignRecipient({
      contact_person_id: selectedContactPersonId,
      cperson_name: quotation?.cperson_name || "",
      cperson_email: quotation?.cperson_email || "",
      cperson_position: quotation?.cperson_position || "",
    });
    await getContacts(quotation?.company_id, selectedContactPersonId);
    setShowSignRequestModal(true);
  };

  const sendSignRequest = async (recipient) => {
    setIsSendingSignRequest(true);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "-10000px";
    iframe.style.bottom = "0";
    iframe.style.width = "1200px";
    iframe.style.height = "1600px";
    iframe.style.border = "0";
    const printParams = new URLSearchParams({
      export: "pdf",
      signerName: recipient.cperson_name || "",
      signerPosition: recipient.cperson_position || "",
      signerDate: recipient.sign_date || "",
    });
    iframe.src = `${window.location.origin}/quotation/print/${id_quotation}?${printParams.toString()}`;
    document.body.appendChild(iframe);

    const waitForIframeLoad = () =>
      new Promise((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          reject(new Error("Timed out while loading quotation print view"));
        }, 15000);

        iframe.onload = () => {
          window.clearTimeout(timeoutId);
          resolve();
        };

        iframe.onerror = () => {
          window.clearTimeout(timeoutId);
          reject(new Error("Failed to load quotation print view"));
        };
      });

    const waitForReadyState = async (iframeDocument) => {
      const maxAttempts = 100;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (iframeDocument.body?.dataset?.quotationReady === "true") {
          return;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 100));
      }
      throw new Error("Quotation print layout did not finish rendering");
    };

    const waitForImages = async (iframeDocument) => {
      const images = Array.from(iframeDocument.images || []);
      await Promise.all(
        images.map(
          (image) =>
            new Promise((resolve) => {
              if (image.complete) {
                resolve();
                return;
              }
              image.onload = resolve;
              image.onerror = resolve;
            }),
        ),
      );
    };

    try {
      await waitForIframeLoad();

      const iframeWindow = iframe.contentWindow;
      const iframeDocument = iframeWindow?.document;
      if (!iframeWindow || !iframeDocument) {
        throw new Error("Unable to access quotation print document");
      }

      await waitForReadyState(iframeDocument);
      await waitForImages(iframeDocument);

      const exportTarget = iframeDocument.querySelector(".invoice-container");
      if (!exportTarget) {
        throw new Error("Quotation print content not found");
      }
      const signatureLine = iframeDocument.querySelector("[data-signature-line='true']");
      if (!signatureLine) {
        throw new Error("Signature line not found in quotation print content");
      }

      const canvas = await html2canvas(exportTarget, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        windowWidth: iframeDocument.documentElement.scrollWidth,
        windowHeight: iframeDocument.documentElement.scrollHeight,
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pdfMargin = 12;
      const footerReserve = 10;
      const contentWidth = pageWidth - pdfMargin * 2;
      const contentHeight = pageHeight - pdfMargin * 2 - footerReserve;
      const pageContentHeightPx = (contentHeight * canvas.width) / contentWidth;
      const exportRect = exportTarget.getBoundingClientRect();
      const signatureRect = signatureLine.getBoundingClientRect();
      const scaleRatio = canvas.width / exportRect.width;
      const signatureXCanvas = (signatureRect.left - exportRect.left) * scaleRatio;
      const signatureYCanvas = (signatureRect.top - exportRect.top) * scaleRatio;
      const protectedBlocks = Array.from(
        exportTarget.querySelectorAll(".invoice-header, .terms-section, .terms-section-2"),
      )
        .map((element) => ({
          top: element.offsetTop,
          bottom: element.offsetTop + element.offsetHeight,
        }))
        .sort((a, b) => a.top - b.top);

      const pageSlices = [];
      let currentStartPx = 0;

      while (currentStartPx < canvas.height) {
        let currentEndPx = Math.min(currentStartPx + pageContentHeightPx, canvas.height);
        const crossingBlock = protectedBlocks.find(
          (block) =>
            block.top < currentEndPx &&
            block.bottom > currentEndPx &&
            block.bottom - block.top <= pageContentHeightPx,
        );

        if (crossingBlock && crossingBlock.top > currentStartPx) {
          currentEndPx = Math.min(crossingBlock.top, canvas.height);
        }

        if (currentEndPx <= currentStartPx) {
          currentEndPx = Math.min(currentStartPx + pageContentHeightPx, canvas.height);
        }

        pageSlices.push({
          start: Math.floor(currentStartPx),
          end: Math.floor(currentEndPx),
        });
        currentStartPx = currentEndPx;
      }

      const signaturePageIndex = pageSlices.findIndex(
        (slice) => signatureYCanvas >= slice.start && signatureYCanvas < slice.end,
      );
      if (signaturePageIndex === -1) {
        throw new Error("Signature line position could not be mapped to a PDF page");
      }

      const mmPerCanvasPx = contentWidth / canvas.width;
      const signatureX = Number((pdfMargin + signatureXCanvas * mmPerCanvasPx).toFixed(2));
      const signatureY = Number(
        (
          pdfMargin +
          (signatureYCanvas - pageSlices[signaturePageIndex].start) * mmPerCanvasPx
        ).toFixed(2),
      );
      const mekariCanvasWidth = 595;
      const mekariCanvasHeight = 841;
      const signatureXMekari = Number(((signatureX / pageWidth) * mekariCanvasWidth).toFixed(2));
      const signatureLineWidthCanvas = signatureRect.width * scaleRatio;
      const signatureWidthMm = signatureLineWidthCanvas * mmPerCanvasPx;
      const signatureWidth = Number(
        Math.min(
          Math.max((signatureWidthMm / pageWidth) * mekariCanvasWidth, 160),
          260,
        ).toFixed(2),
      );
      const signatureHeight = 68;
      const signatureYBaseMekari = Number(((signatureY / pageHeight) * mekariCanvasHeight).toFixed(2));
      const signatureGapAboveLine = 10;
      const signatureYMekari = Number(
        Math.max(signatureYBaseMekari - signatureHeight - signatureGapAboveLine, 0).toFixed(2),
      );
      const imageType = "JPEG";
      const imageQuality = 0.55;

      pageSlices.forEach((slice, index) => {
        if (index > 0) {
          pdf.addPage();
        }

        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = slice.end - slice.start;
        const sliceContext = sliceCanvas.getContext("2d");
        if (!sliceContext) {
          throw new Error("Unable to create PDF slice canvas");
        }

        sliceContext.fillStyle = "#ffffff";
        sliceContext.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        sliceContext.drawImage(
          canvas,
          0,
          slice.start,
          canvas.width,
          sliceCanvas.height,
          0,
          0,
          canvas.width,
          sliceCanvas.height,
        );

        const imageData = sliceCanvas.toDataURL("image/jpeg", imageQuality);
        const sliceHeightMm = (sliceCanvas.height * contentWidth) / sliceCanvas.width;
        pdf.addImage(
          imageData,
          imageType,
          pdfMargin,
          pdfMargin,
          contentWidth,
          sliceHeightMm,
          undefined,
          "FAST",
        );
      });

      const totalPages = pdf.getNumberOfPages();
      pdf.setFontSize(10);
      pdf.setTextColor(120, 120, 120);
      for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
        pdf.setPage(pageNumber);
        pdf.text(
          `Page ${pageNumber} of ${totalPages}`,
          pageWidth / 2,
          pageHeight - 5,
          { align: "center" },
        );
      }

      const fileName = quotation?.quotation_code
        ? `${quotation.quotation_code}.pdf`
        : `quotation-${id_quotation}.pdf`;
      const pdfBlob = pdf.output("blob");
      const payload = new FormData();
      payload.append("pdf", pdfBlob, fileName);
      payload.append("positionX", String(signatureXMekari));
      payload.append("positionY", String(signatureYMekari));
      payload.append("pageNumber", String(signaturePageIndex + 1));
      payload.append("width", String(signatureWidth));
      payload.append("height", String(signatureHeight));
      payload.append("canvasWidth", String(mekariCanvasWidth));
      payload.append("canvasHeight", String(mekariCanvasHeight));
      payload.append("signerName", recipient.cperson_name || "");
      payload.append("signerEmail", recipient.cperson_email || "");
      payload.append("signerPosition", recipient.cperson_position || "");
      payload.append("companyId", String(quotation?.company_id || ""));
      payload.append("subcompanyId", String(quotation?.subcompany_id || ""));
      payload.append("contactPersonId", String(recipient.contact_person_id || ""));

      const fetchSendQuotation = await jwtFetch(config.ENDPOINT + "send/quotation", {
        method: "POST",
        body: payload,
      });
      const responseSendQuotation = await fetchSendQuotation.json();
      if (responseSendQuotation.status === 200) {
        console.log("Send Sign Request response", responseSendQuotation.data);
      } else {
        alert(responseSendQuotation.message);
      }
    } catch (error) {
      console.error("Send Sign Request failed", error);
      alert("Failed to send quotation sign request.");
    } finally {
      document.body.removeChild(iframe);
      setIsSendingSignRequest(false);
    }
  };

  const submitSignRequest = async () => {
    const normalizedName = (signRecipient.cperson_name || "").trim();
    const normalizedEmail = (signRecipient.cperson_email || "").trim();
    if (!normalizedName || !normalizedEmail) {
      alert("Contact name and email are required.");
      return;
    }
    await sendSignRequest({
      contact_person_id: signRecipient.contact_person_id || quotation?.contact_person_id || null,
      cperson_name: normalizedName,
      cperson_email: normalizedEmail,
      cperson_position: (signRecipient.cperson_position || "").trim(),
      sign_date: format(new Date(), "dd MMMM yyyy"),
    });
    setShowSignRequestModal(false);
  };

  const stopBilling = async () => {
    try {
      let termNumber;
      if (iouList) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const activeIous = iouList
          .sort((a, b) => (a.term_number || '').localeCompare(b.term_number || ''))
          .filter((iou) => {
            const startDate = new Date(iou.start_term_period);
            const endDate = new Date(iou.end_term_period);
            return startDate <= today && today <= endDate;
          });

        if (activeIous.length > 0) {
          termNumber = activeIous[0].term_number;
        } else {
          const upcomingious = iouList
            .sort((a, b) => (a.term_number || '').localeCompare(b.term_number || ''))
            .filter((iou) => {
              const startDate = new Date(iou.start_term_period);
              return startDate > today;
            });
          termNumber = upcomingious[0].term_number;
        }
      }
      if (termNumber) {
        let products = [];
        for (let i in quotation.quotation_item_list) {
          products.push({
            product_id: quotation.quotation_item_list[i].product_id,
            product_domain: quotation.quotation_item_list[i].product_domain,
            product_qty: quotation.quotation_item_list[i].product_qty,
          });
        }

        const fetchStopBilling = await jwtFetch(config.ENDPOINT + "stop/billing", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            quotation_id: id_quotation,
            term_number: termNumber,
            service_type: quotation.quotation_item_list[0].service_type,
            company_id: quotation.company_id,
            subcompany_id: quotation.subcompany_id,
            products: products,
          }),
        });
        const responseStopBilling = await fetchStopBilling.json();
        if (responseStopBilling.status === 200) {
          window.location.reload();
        }
      } else {
        alert("No currently active Invoice Requests");
      }
    } catch (error) {
      console.error('Error in stopBilling:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  return (
    <div id="quotation-detail">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard">
                <img className="h-100" src="/cuancuan.png" alt="PointStar" />
              </a>
            </div>
            <hr className="m-0" />
            <Menu active="quotation" />
          </div>
          <div className="col-md-10">
            <Header />
            <hr className="m-0" />
            <div className="main">
              <div className="p-4">
                {quotation && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center">
                        <div className="fs-24">
                          <button className="btn btn-link" onClick={() => navigate(-1)}>
                            <i className="bi bi-arrow-left" />
                          </button>{" "}
                          Quotation Detail
                        </div>
                        <span
                          className={
                            "badge badge-sm ms-3 text-bg-" +
                            (quotation.quotation_status === "Won"
                              ? "success"
                              : quotation.quotation_status === "Canceled" ||
                                quotation.quotation_status === "Lost"
                                ? "danger"
                                : "secondary")
                          }
                        >
                          {quotation.quotation_status}
                        </span>
                        {quotation.quotation_status === "Open" && (
                          <span
                            className={
                              "badge badge-sm ms-1 text-bg-" +
                              (quotation.quotation_approval === "Approved"
                                ? "success"
                                : quotation.quotation_approval === "Rejected"
                                  ? "danger"
                                  : "secondary")
                            }
                          >
                            {quotation.quotation_approval}
                          </span>
                        )}
                        {quotation.quotation_status === "Won" &&
                          (salesOrder ? (
                            salesOrder.so_status === "Completed" ? (
                              <span className={"badge badge-sm ms-1 text-bg-success"}>
                                SO Completed
                              </span>
                            ) : salesOrder.so_status === "In Progress" ? (
                              <span className={"badge badge-sm ms-1 text-bg-warning"}>
                                SO In Progress
                              </span>
                            ) : (
                              <span className={"badge badge-sm ms-1 text-bg-danger"}>
                                SO Cancelled
                              </span>
                            )
                          ) : (
                            <span className={"badge badge-sm ms-1 text-bg-warning"}>
                              SO Not Created
                            </span>
                          ))}
                        {quotation.is_invoicing_only === 1 && (
                          <span className={"badge badge-sm ms-1 text-bg-warning"}>
                            Invoicing Only
                          </span>
                        )}
                      </div>
                      {/* {crmUser.user_department !== "Sales Admin" && ()} */}
                      {crmUser.user_level !== 'View' && (
                        <>


                          {quotation.quotation_status === "Draft" ? (
                            <div className="d-flex align-items-center justify-content-between gap-2">
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => editStatus("Canceled")}
                                disabled={isLoadingSubmit}
                              >
                                <i className="bi bi-x-circle me-1" /> Cancel
                              </button>
                              <a
                                className="btn btn-sm btn-outline-secondary"
                                href={"/quotation/edit/" + id_quotation}
                              >
                                <i className="bi bi-pencil me-1" /> Edit
                              </a>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => editStatus("Open")}
                                disabled={isLoadingSubmit}
                              >
                                <i className="bi bi-send me-1" />{" "}
                                {isLoadingSubmit ? "Loading..." : "Submit"}
                              </button>
                            </div>
                          ) : quotation.quotation_status === "Open" ? (
                            quotation.quotation_approval === "Approved" ? (
                              <div className="d-flex align-items-center justify-content-between gap-2">
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => editStatus("Canceled")}
                                  disabled={isLoadingSubmit}
                                >
                                  <i className="bi bi-x-circle me-1" /> Cancel
                                </button>
                                <a
                                  className="btn btn-sm btn-outline-secondary"
                                  href={"/quotation/view/" + id_quotation}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <i className="bi bi-eye me-1" /> View Form
                                </a>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => printQuotation()}
                                >
                                  <i className="bi bi-printer me-1" /> Print
                                </button>
                                {/* <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => openSignRequestModal()}
                            >
                              <i className="bi bi-pen me-1" /> Send Sign Request
                            </button> */}
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => setShowCloseLost(true)}
                                  disabled={isLoadingSubmit}
                                >
                                  <i className="bi bi-dash-circle me-1" /> Close Lost
                                </button>
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => editStatus("Won")}
                                  disabled={isLoadingSubmit}
                                >
                                  <i className="bi bi-check-circle me-1" /> Close Won
                                </button>
                              </div>
                            ) : quotation.quotation_approval === "Rejected" ? (
                              <div className="d-flex align-items-center justify-content-between gap-2">
                                <a
                                  className="btn btn-sm btn-outline-secondary"
                                  href={"/quotation/edit/" + id_quotation}
                                >
                                  <i className="bi bi-pencil me-1" /> Edit
                                </a>
                              </div>
                            ) : (quotation.quotation_approval === "Pending Approval" || quotation.quotation_approval === "Pending Approval 1" || quotation.quotation_approval === "Pending Approval 2") && isCurrentUserApprover() ? (
                              <div className="d-flex align-items-center justify-content-between gap-2">
                                <button className="btn btn-sm btn-danger" onClick={() => setApprovalStatus("Rejected")} disabled={isLoadingSubmit}><i className="bi bi-x-circle me-2" /> Reject</button>
                                <button className="btn btn-sm btn-success" onClick={() => setApprovalStatus("Approved")} disabled={isLoadingSubmit}><i className="bi bi-check-circle me-2" /> Approve</button>
                              </div>
                            ) : (
                              <div className="d-flex align-items-center justify-content-between gap-2">
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => editStatus("Canceled")}
                                  disabled={isLoadingSubmit}
                                >
                                  <i className="bi bi-x-circle me-1" /> Cancel
                                </button>
                              </div>
                            )
                          ) : quotation.quotation_status === "Won" ? (
                            <div className="d-flex align-items-center justify-content-between gap-2">
                              {iouList &&
                                iouList.length > 0 &&
                                !iouList.some((iou) => iou.iou_status === "Stopped") ? (
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => stopBilling()}
                                >
                                  <i className="bi bi-dash-circle me-1" /> Stop Billing
                                </button>
                              ) : (
                                !(iouList && iouList.some((iou) => iou.iou_status === "Stopped")) &&
                                !(
                                  salesOrder &&
                                  (salesOrder.so_status === "Completed" ||
                                    salesOrder.so_status === "Cancelled")
                                ) && (
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => editStatus("Canceled")}
                                    disabled={isLoadingSubmit}
                                  >
                                    <i className="bi bi-x-circle me-1" /> Cancel
                                  </button>
                                )
                              )}
                              <a
                                className="btn btn-sm btn-outline-secondary"
                                href={"/quotation/view/" + id_quotation}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <i className="bi bi-eye me-1" /> View Form
                              </a>
                              {!salesOrder && (
                                <a
                                  className="btn btn-sm btn-outline-secondary"
                                  href={"/salesorder/add/" + quotation.id_quotation}
                                >
                                  <i className="bi bi-file-earmark-text me-1" /> Create Sales Order
                                </a>
                              )}
                              {!isLoadingRelatedContractData &&
                                !relatedContract &&
                                !relatedSchedule && (
                                  <a
                                    className="btn btn-sm btn-outline-secondary"
                                    href={"/quotation/contract/" + id_quotation}
                                  >
                                    <i className="bi bi-file-earmark-medical me-2" /> Create Contract
                                  </a>
                                )}
                            </div>
                          ) : (
                            ""
                          )}
                        </>
                      )}
                      {(quotation.quotation_status === "Freeze" ||
                        quotation.quotation_status === "Expire") &&
                        crmUser.user_level === "Admin" && (
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => reopenStatus()}
                              disabled={isLoadingSubmit}
                            >
                              <i className="bi bi-inbox-fill me-1" />{" "}
                              {isLoadingSubmit ? "Loading..." : "Re-Open"}
                            </button>
                          </div>
                        )}
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Number</div>
                          <div className="col-md-8">{quotation.quotation_code}</div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Company Group</div>
                          <div className="col-md-8">
                            {quotation.company_name}{" "}
                            <a href={`/client/detail/${quotation.company_id}`}>
                              <i className="bi bi-link-45deg" />
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Subsidiary</div>
                          <div className="col-md-8">
                            {quotation.subcompany_name}{" "}
                            <a href={`/client/subsidiary/detail/${quotation.subcompany_id}`}>
                              <i className="bi bi-link-45deg" />
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Currency</div>
                          <div className="col-md-8">{quotation.quotation_currency}</div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Exchange Rate</div>
                          <div className="col-md-8">
                            1 USD <i className="bi bi-arrow-right mx-1" />{" "}
                            {priceFormat(
                              quotation.quotation_exchange_rate,
                              quotation.quotation_currency,
                            )}{" "}
                            ({quotation.quotation_exchange_floating})
                          </div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Created Date</div>
                          <div className="col-md-8">
                            {format(quotation.initial_date, "d MMMM yyyy")}
                          </div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Effective Date</div>
                          <div className="col-md-8">
                            {format(quotation.created_on, "d MMMM yyyy")}
                          </div>
                        </div>
                        {quotation.quotation_status === "Draft" ||
                          quotation.quotation_status === "Open" ? (
                          <div className="row mb-2">
                            <div className="col-md-4 text-secondary">Expiry Date</div>
                            <div className="col-md-8">
                              {format(quotation.quotation_expired_date, "d MMMM yyyy")}
                            </div>
                          </div>
                        ) : (
                          ""
                        )}
                        {quotation.quotation_item_list[0].product_active_licence_date && (
                          <div className="row mb-2">
                            <div className="col-md-4 text-secondary">Activation Date</div>
                            <div className="col-md-8">
                              {format(
                                quotation.quotation_item_list[0].product_active_licence_date,
                                "d MMMM yyyy",
                              )}
                            </div>
                          </div>
                        )}
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Order Type</div>
                          <div className="col-md-8">
                            {quotation.quotation_item_list[0].service_type}
                            {Array.isArray(quotation.quotation_item_list) &&
                              quotation.quotation_item_list.some((item) => item.is_upgrade) &&
                              " (Upgrade)"}
                          </div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Sales Name</div>
                          <div className="col-md-8">{quotation.sales_name}</div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        {quotation.quotation_approval !== "Approved" &&
                          (quotation.quotation_margin_rate <= 20 && quotation.approval_1_remarks ? (
                            <div className="card mb-2">
                              <div className="card-body">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <p className="fs-14 mb-0">
                                    <strong>Approval 1 Remarks</strong>
                                  </p>
                                </div>
                                <div className="p-2">{quotation.approval_1_remarks}</div>
                              </div>
                            </div>
                          ) : (
                            quotation.quotation_margin_rate <= 25 &&
                            quotation.approval_1_remarks && (
                              <div className="card mb-2">
                                <div className="card-body">
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <p className="fs-14 mb-0">
                                      <strong>Approval Remarks</strong>
                                    </p>
                                  </div>
                                  <div className="p-2">{quotation.approval_1_remarks}</div>
                                </div>
                              </div>
                            )
                          ))}
                        {quotation.quotation_margin_rate <= 20 && quotation.approval_2_remarks && (
                          <div className="card mb-2">
                            <div className="card-body">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <p className="fs-14 mb-0">
                                  <strong>Approval 2 Remarks</strong>
                                </p>
                              </div>
                              <div className="p-2">{quotation.approval_2_remarks}</div>
                            </div>
                          </div>
                        )}
                        {quotation.opportunity_code && (
                          <div className="card mb-2">
                            <div className="card-body">
                              <div className="d-flex align-items-center justify-content-between">
                                <p className="fs-14 mb-0">
                                  <strong>Opportunity</strong>
                                </p>
                                <a href={"/opportunity/detail/" + quotation.opportunity_id}>
                                  {quotation.opportunity_code} <i className="bi bi-link-45deg ms-1" />
                                </a>
                              </div>
                            </div>
                          </div>
                        )}
                        {relatedContract && (
                          <div className="card mb-2">
                            <div className="card-body">
                              <div className="d-flex align-items-center justify-content-between">
                                <p className="fs-14 mb-0">
                                  <strong>Contract</strong>
                                </p>
                                <a href={"/client/contract/detail/" + relatedContract.id_contract}>
                                  {relatedContract.contract_name || `Contract #${relatedContract.id_contract}`}{" "}
                                  <i className="bi bi-link-45deg ms-1" />
                                </a>
                              </div>
                            </div>
                          </div>
                        )}
                        {relatedSchedule && (
                          <div className="card mb-2">
                            <div className="card-body">
                              <div className="d-flex align-items-center justify-content-between">
                                <p className="fs-14 mb-0">
                                  <strong>Contract Schedule</strong>
                                </p>
                                <a href={"/client/contract/schedule/" + relatedSchedule.id_contract_schedule}>
                                  {`Schedule #${relatedSchedule.id_contract_schedule}`}{" "}
                                  <i className="bi bi-link-45deg ms-1" />
                                </a>
                              </div>
                            </div>
                          </div>
                        )}
                        {salesOrder && (
                          <div className="card mb-2">
                            <div className="card-body">
                              <div className="d-flex align-items-center justify-content-between">
                                <p className="fs-14 mb-0">
                                  <strong>Sales Order</strong>
                                </p>
                                <a href={"/salesorder/detail/" + salesOrder.id_so}>
                                  {salesOrder.so_code} <i className="bi bi-link-45deg ms-1" />
                                </a>
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="card mb-2">
                          <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <p className="fs-14 mb-0">
                                <strong>Cost &amp; Profit Summary</strong>
                              </p>
                            </div>
                            <div className="p-2">
                              <div className="row">
                                <div className="col-md-6 text-secondary">Total Cost</div>
                                <div className="col-md-6 text-end">
                                  {priceFormat(
                                    quotation.quotation_cost,
                                    quotation.quotation_currency,
                                  )}
                                </div>
                              </div>
                              <hr />
                              <div className="row mb-2">
                                <div className="col-md-6 text-secondary">
                                  Subtotal Sell (Pre-Discount, Pre-Tax)
                                </div>
                                <div className="col-md-6 text-end">
                                  {priceFormat(
                                    quotation.quotation_subtotal,
                                    quotation.quotation_currency,
                                  )}
                                </div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-6 text-secondary">
                                  Discount on QT{" "}
                                  {quotation.quotation_discount_price > 0
                                    ? " (" + priceFormat(quotation.quotation_discount_rate) + "%)"
                                    : ""}
                                </div>
                                <div className="col-md-6 text-end">
                                  <span className="text-danger">
                                    {quotation.quotation_discount_price > 0
                                      ? "-" +
                                      priceFormat(
                                        quotation.quotation_discount_price,
                                        quotation.quotation_currency,
                                      )
                                      : "0"}
                                  </span>
                                </div>
                              </div>
                              <div className="row">
                                <div className="col-md-6 text-secondary">Net Amount Before Tax</div>
                                <div className="col-md-6 text-end">
                                  {priceFormat(
                                    quotation.quotation_total,
                                    quotation.quotation_currency,
                                  )}
                                </div>
                              </div>
                              <hr />
                              <div className="row">
                                <div className="col-md-6 text-secondary">
                                  Estimated Gross Profit{" "}
                                  <strong>{" (" + quotation.quotation_margin_rate + "%)"}</strong>
                                </div>
                                <div className="col-md-6 text-end">
                                  {priceFormat(
                                    quotation.quotation_margin_price,
                                    quotation.quotation_currency,
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {quotation && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">SKU List</div>
                    </div>
                    <div className="bg-white border-radius-4 p-4">
                      <div className="table-responsive">
                        <table className="table table-hover bordered mb-0">
                          <thead>
                            <tr>
                              <th className="bg-gold w-30">#</th>
                              <th className="bg-gold">SKUs</th>
                              <th className="bg-gold w-85">Period</th>
                              <th className="bg-gold w-85">Qty</th>
                              <th className="bg-gold w-105">Cost Price / Unit</th>
                              <th className="bg-gold w-105">Sell Price / Unit</th>
                              <th className="bg-gold w-105">Discount</th>
                              <th className="bg-gold w-125">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quotation.quotation_item_list &&
                              quotation.quotation_item_list.map((item, i) => {
                                const iouItem = iouList?.[0]?.iou_items?.find(
                                  (iouItem) =>
                                    iouItem.product_id ===
                                    quotation.quotation_item_list[i].product_id,
                                );
                                return (
                                  <tr className={item.product_total === 0 ? "d-none" : ""} key={i}>
                                    <td valign="middle" align="center">
                                      {i + 1}
                                    </td>
                                    <td valign="middle">
                                      <div className="d-flex align-items-center justify-content-between mb-2">
                                        <p className="mb-0">
                                          <strong>{item.product_display_name}</strong>
                                        </p>
                                        <button
                                          className="btn btn-sm btn-outline-secondary"
                                          onClick={() => copyItem(i)}
                                        >
                                          <i className="bi bi-copy" />
                                        </button>
                                      </div>
                                      {item.product_display_description &&
                                        item.product_display_description
                                          .split("\n")
                                          .map((description, d) => (
                                            <div className="row mb-1" key={"description-" + d}>
                                              {description.split(":").length > 1 ? (
                                                <div className="col-4">
                                                  {description.split(":")[0].trim()}
                                                </div>
                                              ) : (
                                                <div className="col-12">{description.trim()}</div>
                                              )}
                                              {description.split(":").length > 1 && (
                                                <div className="col-8">
                                                  <div className="d-flex">
                                                    <span className="me-2">:</span>
                                                    <div>
                                                      {description
                                                        .split(":")
                                                        .slice(1)
                                                        .join(":")
                                                        .trim()}
                                                    </div>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                      {iouItem && iouItem.product_type === "Subscription" && (
                                        <div className="text-secondary">
                                          <hr />
                                          {iouItem.service_type === "Additional" && (
                                            <div className="row mb-1">
                                              <div className="col-4">
                                                Period - Current Active Subscription
                                              </div>
                                              <div className="col-8">
                                                <div className="d-flex">
                                                  <span className="me-2">:</span>
                                                  <div>
                                                    {iouItem.licence_start_period
                                                      ? format(
                                                        new Date(iouItem.licence_start_period),
                                                        "d MMMM yyyy",
                                                      ) +
                                                      " - " +
                                                      format(
                                                        new Date(iouItem.licence_end_period),
                                                        "d MMMM yyyy",
                                                      )
                                                      : ""}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          <div className="row mb-1">
                                            <div className="col-4">Period - for this QT Order</div>
                                            <div className="col-8">
                                              <div className="d-flex">
                                                <span className="me-2">:</span>
                                                <div>
                                                  {format(
                                                    new Date(iouItem.start_term_period),
                                                    "d MMMM yyyy",
                                                  ) +
                                                    " - " +
                                                    format(
                                                      new Date(iouItem.end_term_period),
                                                      "d MMMM yyyy",
                                                    )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      {item.product_tagging && (
                                        <div className="text-secondary">
                                          <hr />
                                          <div className="row mb-1">
                                            <div className="col-4">Tagging</div>
                                            <div className="col-8">
                                              <div className="d-flex">
                                                <span className="me-2">:</span>
                                                <div>{item.product_tagging}</div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </td>
                                    <td valign="middle" align="center">
                                      {item.product_type === "Subscription"
                                        ? item.contract_long_period +
                                        " " +
                                        (item.contract_long_period > 1 ? " Months" : " Month")
                                        : "N/A"}
                                    </td>
                                    <td valign="middle" align="center">
                                      {item.product_qty + " Unit(s)"}
                                    </td>
                                    <td valign="middle" align="center">
                                      <div className="d-flex flex-column justify-content-between py-3">
                                        <span>
                                          {priceFormat(item.product_cost, item.product_currency)}
                                        </span>
                                        {item.product_currency !==
                                          item.product_currency_original && <hr className="my-3" />}
                                        {item.product_currency !==
                                          item.product_currency_original && (
                                            <span className="text-secondary">
                                              {priceFormat(
                                                item.product_cost_original,
                                                item.product_currency_original,
                                              )}
                                            </span>
                                          )}
                                      </div>
                                    </td>
                                    <td valign="middle" align="center">
                                      <div className="d-flex flex-column justify-content-between py-3">
                                        <span>
                                          {priceFormat(item.product_price, item.product_currency)}
                                        </span>
                                        {item.product_currency !==
                                          item.product_currency_original && <hr className="my-3" />}
                                        {item.product_currency !==
                                          item.product_currency_original && (
                                            <span className="text-secondary">
                                              {priceFormat(
                                                item.product_price_original,
                                                item.product_currency_original,
                                              )}
                                            </span>
                                          )}
                                      </div>
                                    </td>
                                    <td valign="middle" align="center">
                                      {item.product_discount_price > 0 ? (
                                        <div className="d-flex flex-column justify-content-between py-3">
                                          <span className="text-danger">
                                            -
                                            {priceFormat(
                                              item.product_discount_price,
                                              item.product_currency,
                                            )}
                                          </span>
                                          {item.product_discount_price > 0 && (
                                            <hr className="my-3" />
                                          )}
                                          {item.product_discount_price > 0 && (
                                            <span className="text-secondary">
                                              {priceFormat(item.product_discount_rate, "%")}
                                            </span>
                                          )}
                                        </div>
                                      ) : (
                                        "N/A"
                                      )}
                                    </td>
                                    <td valign="middle" align="end">
                                      <span>
                                        {priceFormat(item.product_total, item.product_currency)}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td className="text-end" colSpan={7}>
                                Subtotal
                                <br />
                                Lumpsum Discount on QT{" "}
                                {quotation.quotation_discount_price > 0
                                  ? " (" + priceFormat(quotation.quotation_discount_rate) + "%)"
                                  : ""}
                                <br />
                                Total Before Tax
                              </td>
                              <td className="text-end">
                                {priceFormat(
                                  quotation.quotation_subtotal,
                                  quotation.quotation_currency,
                                )}
                                <br />
                                <span className="text-danger">
                                  {quotation.quotation_discount_price > 0
                                    ? "-" +
                                    priceFormat(
                                      quotation.quotation_discount_price,
                                      quotation.quotation_currency,
                                    )
                                    : "0"}
                                </span>
                                <br />
                                <strong>
                                  {priceFormat(
                                    quotation.quotation_total,
                                    quotation.quotation_currency,
                                  )}
                                </strong>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {!(iouList && iouList.length > 0) && quotation && paymentSimulations && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">Payment Schedules</div>
                    </div>
                    <div className="bg-white border-radius-4 p-4">
                      <div className="table-responsive">
                        <table className="table table-hover bordered mb-0">
                          <thead>
                            <tr>
                              <th className="bg-gold w-90">Schedule</th>
                              <th className="bg-gold w-200">Date</th>
                              <th className="bg-gold text-start">SKUs</th>
                              <th className="bg-gold w-125">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quotation.quotation_items &&
                              (() => {
                                const termGroups = {};
                                quotation.quotation_items.forEach((item) => {
                                  item.payment_simulations.forEach((payment) => {
                                    const key = `${payment.term_number}-${payment.number_of_months}`;
                                    if (!termGroups[key]) {
                                      termGroups[key] = {
                                        term_number: payment.term_number,
                                        number_of_months: payment.number_of_months,
                                        start_term_period: payment.start_term_period,
                                        end_term_period: payment.end_term_period,
                                        items: [],
                                      };
                                    }
                                    termGroups[key].items.push({
                                      product_display_name: item.product_display_name,
                                      payment_amount_exc_tax: payment.payment_amount_exc_tax,
                                    });
                                  });
                                });
                                const sortedTerms = Object.values(termGroups).sort(
                                  (a, b) => a.term_number - b.term_number,
                                );
                                return sortedTerms
                                  .map((term, termIndex) => {
                                    const rowspan = term.items.length;
                                    return term.items.map((item, itemIndex) => (
                                      <tr key={`term-${termIndex}-item-${itemIndex}`}>
                                        {itemIndex === 0 && (
                                          <>
                                            <td rowSpan={rowspan} valign="middle" align="center">
                                              {term.term_number.replace("Term", "Schedule")}
                                            </td>
                                            <td rowSpan={rowspan} valign="middle" align="center">
                                              <span>
                                                {format(
                                                  new Date(term.start_term_period),
                                                  "d MMM yyyy",
                                                ) +
                                                  " - " +
                                                  format(
                                                    new Date(term.end_term_period),
                                                    "d MMM yyyy",
                                                  )}
                                              </span>
                                            </td>
                                          </>
                                        )}
                                        <td>{item.product_display_name}</td>
                                        <td style={{ textAlign: "right" }}>
                                          {priceFormat(
                                            item.payment_amount_exc_tax,
                                            quotation.quotation_currency,
                                          )}
                                        </td>
                                      </tr>
                                    ));
                                  })
                                  .flat();
                              })()}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td className="text-end" colSpan={3}>
                                Total Before Tax
                              </td>
                              <td className="text-end">
                                <strong>
                                  {(() => {
                                    const totalExcTax = (quotation.quotation_items || []).reduce(
                                      (sum, item) => {
                                        const itemTotal = (item.payment_simulations || []).reduce(
                                          (s, p) => {
                                            const val = parseFloat(p.payment_amount_exc_tax) || 0;
                                            return s + val;
                                          },
                                          0,
                                        );
                                        return sum + itemTotal;
                                      },
                                      0,
                                    );
                                    return priceFormat(totalExcTax, quotation.quotation_currency);
                                  })()}
                                </strong>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {quotation && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">Attachments</div>
                    </div>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="row align-items-top form-group mb-2">
                          <div className="col-md-2">
                            <label>Files</label>
                          </div>
                          <div className="col-md-10">
                            {JSON.parse(quotation.attachment_files) && (
                              <div className="table-responsive">
                                <table className="table table-hover bordered mb-2">
                                  <thead>
                                    <tr>
                                      <th className="bg-gold w-30">#</th>
                                      <th className="bg-gold text-start">Name</th>
                                      <th className="bg-gold text-start">File</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {JSON.parse(quotation.attachment_files).map(
                                      (attachments, a) => (
                                        <tr key={"attachment-" + a}>
                                          <td valign="middle" align="center">
                                            {a + 1}
                                          </td>
                                          <td valign="middle">{attachments.name}</td>
                                          <td valign="middle">
                                            {Array.isArray(attachments.files) &&
                                              attachments.files.length > 0 && (
                                                <a
                                                  className="ellipsis"
                                                  href={attachments.files[0].fileUrl}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                >
                                                  {shortenUrl(attachments.files[0].fileUrl, 50)}{" "}
                                                  <i className="bi bi-link-45deg ms-2" />
                                                </a>
                                              )}
                                          </td>
                                        </tr>
                                      ),
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-2">
                            <label>Quotation Remarks</label>
                          </div>
                          <div className="col-md-10">{quotation.quotation_remarks}</div>
                        </div>
                        {salesOrder && (
                          <div className="row align-items-baseline form-group mb-2">
                            <div className="col-md-2">
                              <label>Sales Order Remarks</label>
                            </div>
                            <div className="col-md-10">{salesOrder.so_remarks}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {iouList && iouList.length > 0 && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">Invoice Requests</div>
                    </div>
                    {termList &&
                      Object.keys(termList)
                        .sort((a, b) => {
                          const getTermNumber = (term) => parseInt(term.split(" ")[1], 10);
                          return getTermNumber(a) - getTermNumber(b);
                        })
                        .map((term, index) => {
                          const isUpcoming =
                            new Date(termList[term][0].start_term_period) >= new Date() &&
                            new Date(termList[term][0].start_term_period) <= addDays(new Date(), 7);
                          const isOverdue =
                            new Date(termList[term][0].start_term_period) <= new Date();
                          return (
                            <Accordion key={index}>
                              <Accordion.Item eventKey={index} className="mb-2">
                                <Accordion.Header>
                                  <strong className="fs-14">
                                    {term.replace("Term", "Schedule")} &emsp;|&emsp;{" "}
                                    {termList[term][0].start_term_period ===
                                      termList[term][0].end_term_period
                                      ? format(termList[term][0].start_term_period, "dd MMM yyyy")
                                      : format(termList[term][0].start_term_period, "dd MMM yyyy") +
                                      " - " +
                                      format(termList[term][0].end_term_period, "dd MMM yyyy")}
                                  </strong>
                                  {termList[term][0].iou_status === "New" && (
                                    <span className="badge bg-secondary text-light ms-2">
                                      Not Billed
                                    </span>
                                  )}
                                  {termList[term][0].iou_status === "New" && isUpcoming && (
                                    <span className="badge bg-warning text-light ms-2">
                                      Upcoming
                                    </span>
                                  )}
                                  {termList[term][0].iou_status === "New" && isOverdue && (
                                    <span className="badge bg-danger text-light ms-2">Overdue</span>
                                  )}
                                  {termList[term][0].iou_status === "In Progress" && (
                                    <span className="badge bg-warning text-dark ms-2">
                                      Admin Validation
                                    </span>
                                  )}
                                  {termList[term][0].iou_status === "Notification Sent" && (
                                    <span className="badge bg-info text-dark ms-2">
                                      Requested to Finance
                                    </span>
                                  )}
                                  {termList[term][0].iou_status === "Invoice Issued" && (
                                    <span className="badge bg-gold text-dark ms-2">
                                      Invoice Issued
                                    </span>
                                  )}
                                  {termList[term][0].iou_status === "Paid in Full" && (
                                    <span className="badge bg-success text-light ms-2">
                                      Paid in Full
                                    </span>
                                  )}
                                  {termList[term][0].iou_status === "Stopped" && (
                                    <span className="badge bg-danger text-light ms-2">Stopped</span>
                                  )}
                                </Accordion.Header>
                                <Accordion.Body>
                                  <div className="table-responsive mb-2">
                                    <table className="table table-hover bordered mb-0">
                                      <thead>
                                        <tr>
                                          <th className="bg-gold w-30">#</th>
                                          {termList[term][0].iou_code && (
                                            <th className="bg-gold w-210">Number</th>
                                          )}
                                          {termList[term][0].invoice_number && (
                                            <th className="bg-gold w-120">Invoice</th>
                                          )}
                                          <th className="bg-gold">Subsidiary</th>
                                          <th className="bg-gold w-120">Period</th>
                                          <th className="bg-gold w-125 text-end">Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {termList[term]
                                          .filter((iou) => iou.iou_status !== "Deleted")
                                          .sort(
                                            (a, b) =>
                                              new Date(b.created_on) - new Date(a.created_on),
                                          )
                                          .map((iou, i) => (
                                            <tr key={"iou-" + i}>
                                              <td align="center">{i + 1}</td>
                                              {termList[term][0].iou_code && (
                                                <td align="center">
                                                  <a
                                                    href={`/invoicerequest/detail/${iou.id_iou}`}
                                                    rel="noopener noreferrer"
                                                  >
                                                    {iou.iou_code}{" "}
                                                    <i className="bi bi-link-45deg ms-2" />
                                                  </a>
                                                </td>
                                              )}
                                              {termList[term][0].invoice_number && (
                                                <td>{iou.invoice_number}</td>
                                              )}
                                              <td>{iou.subcompany_name}</td>
                                              <td align="center">
                                                {iou.number_of_months > 1
                                                  ? iou.number_of_months + " Months"
                                                  : iou.number_of_months + " Month"}
                                              </td>
                                              <td align="right">
                                                {priceFormat(iou.iou_subtotal, iou.iou_currency)}
                                              </td>
                                            </tr>
                                          ))}
                                        <tr>
                                          <td
                                            className="text-end"
                                            colSpan={
                                              termList[term][0].iou_code
                                                ? termList[term][0].invoice_number
                                                  ? 5
                                                  : 4
                                                : 3
                                            }
                                          >
                                            Total Before Tax
                                          </td>
                                          <td className="text-end">
                                            <strong>
                                              {priceFormat(
                                                termList[term].reduce(
                                                  (sum, iou) => sum + parseFloat(iou.iou_subtotal),
                                                  0,
                                                ),
                                                termList[term][0].iou_currency,
                                              )}
                                            </strong>
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                  {/* {!termList[term][0].iou_code && termList[term][0].iou_status === 'Stopped' && ( */}
                                  {termList[term][0].iou_status === "New" &&
                                    crmUser.user_department === "Sales" && (
                                      <div className="d-flex justify-content-end">
                                        <a
                                          href={"/quotation/atb/" + id_quotation + "/" + term}
                                          className="btn btn-sm btn-outline-secondary"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          Bill Now <i className="bi bi-arrow-right ms-2" />
                                        </a>
                                      </div>
                                    )}
                                  {termList[term][0].iou_status === "In Progress" &&
                                    crmUser.user_department === "Sales Admin" && (
                                      <div className="d-flex justify-content-end">
                                        <a
                                          href={
                                            "/quotation/atb/detail/" + id_quotation + "/" + term
                                          }
                                          className="btn btn-sm btn-outline-secondary"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          Validate <i className="bi bi-arrow-right ms-2" />
                                        </a>
                                      </div>
                                    )}
                                </Accordion.Body>
                              </Accordion.Item>
                            </Accordion>
                          );
                        })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <Modal show={showCloseLost} onHide={() => setShowCloseLost(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Close Lost Remarks</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row align-items-baseline form-group mb-2">
              <div className="col-md-4">
                <label>
                  Remarks<span className="text-danger">*</span>
                </label>
              </div>
              <div className="col-md-8">
                <textarea
                  className="form-control"
                  rows="4"
                  value={closeLostRemarks}
                  onChange={(e) => setCloseLostRemarks(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <button
                className="btn btn-link"
                type="button"
                onClick={() => setShowCloseLost(false)}
              >
                Cancel
              </button>
              <button className="btn btn-danger" type="button" onClick={() => editStatus("Lost")}>
                Close Lost
              </button>
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          show={showSignRequestModal}
          onHide={() => !isSendingSignRequest && setShowSignRequestModal(false)}
        >
          <Modal.Header closeButton={!isSendingSignRequest}>
            <Modal.Title>Send Sign Request</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row align-items-baseline form-group mb-2">
              <div className="col-md-4">
                <label>Contact Person</label>
              </div>
              <div className="col-md-8">
                <Select
                  options={contactOptions}
                  value={selectedContactOption}
                  isLoading={isLoadingContacts}
                  isClearable
                  placeholder="Select contact person"
                  onChange={(option) => {
                    setSelectedContactOption(option || null);
                    if (!option) {
                      setSignRecipient((prev) => ({
                        ...prev,
                        contact_person_id: quotation?.contact_person_id || null,
                      }));
                      return;
                    }
                    setSignRecipient((prev) => ({
                      ...prev,
                      contact_person_id: option.contact.id_contact_person,
                      cperson_name: option.contact.cperson_name || "",
                      cperson_email: option.contact.cperson_email || "",
                      cperson_position: option.contact.cperson_position || "",
                    }));
                  }}
                />
              </div>
            </div>
            <div className="row align-items-baseline form-group mb-2">
              <div className="col-md-4">
                <label>
                  Contact Name<span className="text-danger">*</span>
                </label>
              </div>
              <div className="col-md-8">
                <input
                  className="form-control"
                  type="text"
                  value={signRecipient.cperson_name}
                  onChange={(e) =>
                    setSignRecipient((prev) => ({
                      ...prev,
                      cperson_name: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="row align-items-baseline form-group mb-2">
              <div className="col-md-4">
                <label>
                  Contact Email<span className="text-danger">*</span>
                </label>
              </div>
              <div className="col-md-8">
                <input
                  className="form-control"
                  type="email"
                  value={signRecipient.cperson_email}
                  onChange={(e) =>
                    setSignRecipient((prev) => ({
                      ...prev,
                      cperson_email: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="row align-items-baseline form-group mb-2">
              <div className="col-md-4">
                <label>Contact Position</label>
              </div>
              <div className="col-md-8">
                <input
                  className="form-control"
                  type="text"
                  value={signRecipient.cperson_position}
                  onChange={(e) =>
                    setSignRecipient((prev) => ({
                      ...prev,
                      cperson_position: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <button
                className="btn btn-link"
                type="button"
                onClick={() => setShowSignRequestModal(false)}
                disabled={isSendingSignRequest}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                type="button"
                onClick={() => submitSignRequest()}
                disabled={isSendingSignRequest}
              >
                {isSendingSignRequest ? "Sending..." : "Send"}
              </button>
            </div>
          </Modal.Body>
        </Modal>

        <Modal show={showApprovalModal} onHide={() => setShowApprovalModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Reject Quotation</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row align-items-baseline form-group mb-2">
              <div className="col-md-4">
                <label>Remarks <span className="text-danger">*</span></label>
              </div>
              <div className="col-md-8">
                <textarea className="form-control" rows="4" value={approvalRemarks} onChange={(e) => setApprovalRemarks(e.target.value)} required />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <button className="btn btn-link" type="button" onClick={() => setShowApprovalModal(false)}>Cancel</button>
              <button className="btn btn-danger" type="button" onClick={() => submitApproval()}>Reject</button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default QuotationDetail;


